import * as React from 'react';
import { Snowflake } from 'lucide-react';
import {
  Callout,
  WorkspaceButton,
  WorkspaceDialog,
  WorkspaceField,
  WorkspaceSelect,
  useToast,
} from '@/components/ui/workspace';
import { ErrorMessage } from '@/components/ui/error-message';
import { Spinner } from '@/components/ui/spinner';
import {
  useCancelSignoffMutation,
  useRequestSignoffMutation,
  useResendSignoffMutation,
  useWaiveSignoffMutation,
  type SignoffStatus,
} from '@/lib/api/requirements-signoff';
import { usePeopleQuery } from '@/lib/api/discovery-sessions';
import { useAuth } from '@/lib/auth/store';
import { WAIVER_MIN_CHARS, defaultSignoffMessage } from './signoff-logic';

/**
 * The sign-off dialogs (prototype `sign-request` and `sign-waive`, plus
 * confirmations for resend and cancel). Each confirmed action sends one
 * write; the server checks who may, and the checklist, again.
 */

const TEXTAREA =
  'block w-full resize-y rounded-card border border-ink-200 bg-paper-0 px-3 py-2 text-body leading-5 text-ink-900 placeholder:text-ink-300 hover:border-ink-300 focus:outline focus:outline-2 focus:outline-offset-2 focus:outline-accent-amber-600';

const WHO_MAY = 'The engagement lead and firm admins, the same people who may waive';

export function RequestSignoffDialog({
  engagementId,
  status,
  onClose,
}: {
  engagementId: string;
  status: SignoffStatus;
  onClose: () => void;
}) {
  const { toast } = useToast();
  const me = useAuth((s) => s.identity?.displayName) || 'You';
  const people = usePeopleQuery(engagementId);
  const request = useRequestSignoffMutation(engagementId);
  // Anyone in the client's people with an email (Decision 54.39).
  const candidates = React.useMemo(
    () =>
      (people.data?.items ?? []).filter((p) => p.status === 'active' && !p.merged_into && p.email),
    [people.data],
  );
  const [personId, setPersonId] = React.useState<string>(status.signatory?.person_id ?? '');
  const chosen = candidates.find((p) => p.id === personId) ?? null;
  const [message, setMessage] = React.useState<string | null>(null);
  const text = message ?? (chosen ? defaultSignoffMessage(chosen.full_name) : '');

  React.useEffect(() => {
    if (!personId && candidates.length > 0) setPersonId(candidates[0]!.id);
  }, [personId, candidates]);

  const send = () => {
    if (!chosen) return;
    request.mutate(
      { signatory_person_id: chosen.id, message: text.trim() },
      {
        onSuccess: () => {
          toast({
            message: `Snapshot frozen and a secure link sent to ${chosen.full_name}.`,
          });
          onClose();
        },
      },
    );
  };

  return (
    <WorkspaceDialog
      open
      onOpenChange={(open) => !open && onClose()}
      title="Request client sign-off"
      description="Caliber freezes a snapshot of the requirements and sends the signatory a secure link."
      closeOnScrimClick={!request.isPending}
      actions={
        <>
          <WorkspaceButton variant="tertiary" onClick={onClose} disabled={request.isPending}>
            Cancel
          </WorkspaceButton>
          <WorkspaceButton variant="primary" disabled={!chosen || request.isPending} onClick={send}>
            {request.isPending ? 'Sending…' : 'Send secure link'}
          </WorkspaceButton>
        </>
      }
    >
      {people.isPending ? (
        <div className="flex items-center gap-2 text-body-sm text-ink-500">
          <Spinner /> Loading the client’s people…
        </div>
      ) : people.error ? (
        <ErrorMessage error={people.error} />
      ) : candidates.length === 0 ? (
        <p className="text-body-sm text-ink-700">
          None of the client’s people has an email yet. Add one to the person who will sign, in
          Sessions → People, then come back.
        </p>
      ) : (
        <>
          <WorkspaceField
            label="Signatory"
            help="Anyone in the client’s people with an email. They sign from their own secure link."
          >
            {(id, describedBy) => (
              <WorkspaceSelect
                id={id}
                aria-describedby={describedBy}
                value={personId}
                onChange={(e) => {
                  setPersonId(e.target.value);
                  setMessage(null);
                }}
              >
                {candidates.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.title ? `${p.full_name}, ${p.title}` : p.full_name}
                  </option>
                ))}
              </WorkspaceSelect>
            )}
          </WorkspaceField>
          <WorkspaceField label="Message">
            {(id) => (
              <textarea
                id={id}
                rows={3}
                maxLength={2_000}
                value={text}
                onChange={(e) => setMessage(e.target.value)}
                className={TEXTAREA}
              />
            )}
          </WorkspaceField>
        </>
      )}
      <Callout tone="tip" icon={Snowflake}>
        The snapshot is write-once. Any change to the requirements after this reopens sign-off.
      </Callout>
      <WhoGrid
        rows={[
          ['Requested by', me],
          ['Allowed for', WHO_MAY],
        ]}
      />
      {request.error ? <ErrorMessage error={request.error} /> : null}
    </WorkspaceDialog>
  );
}

export function WaiveSignoffDialog({
  engagementId,
  onClose,
}: {
  engagementId: string;
  onClose: () => void;
}) {
  const { toast } = useToast();
  const me = useAuth((s) => s.identity?.displayName) || 'You';
  const waive = useWaiveSignoffMutation(engagementId);
  const [reason, setReason] = React.useState('');
  const len = reason.trim().length;
  const short = WAIVER_MIN_CHARS - len;

  return (
    <WorkspaceDialog
      open
      onOpenChange={(open) => !open && onClose()}
      title="Waive sign-off"
      description="Use only when the client cannot sign. The waiver is permanent, attributed to you, and printed on every exported packet."
      closeOnScrimClick={!waive.isPending}
      actions={
        <>
          <WorkspaceButton variant="tertiary" onClick={onClose} disabled={waive.isPending}>
            Cancel
          </WorkspaceButton>
          <WorkspaceButton
            variant="destructive"
            disabled={short > 0 || waive.isPending}
            onClick={() =>
              waive.mutate(
                { reason: reason.trim() },
                {
                  onSuccess: () => {
                    toast({
                      message: 'Sign-off waived. The reason is printed on every exported packet.',
                    });
                    onClose();
                  },
                },
              )
            }
          >
            {waive.isPending ? 'Waiving…' : 'Waive sign-off'}
          </WorkspaceButton>
        </>
      }
    >
      <WorkspaceField
        label={
          <>
            Reason <span className="text-posture-critical">*</span>
          </>
        }
        help={short > 0 ? `${short} more ${short === 1 ? 'character' : 'characters'} needed` : 'OK'}
      >
        {(id, describedBy) => (
          <textarea
            id={id}
            aria-describedby={describedBy}
            rows={4}
            maxLength={2_000}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Why sign-off is being waived, and who at the client agreed"
            className={TEXTAREA}
          />
        )}
      </WorkspaceField>
      <WhoGrid
        rows={[
          ['Waived by', me],
          ['Allowed for', 'Engagement leads and firm admins'],
        ]}
      />
      {waive.error ? <ErrorMessage error={waive.error} /> : null}
    </WorkspaceDialog>
  );
}

export function ResendSignoffDialog({
  engagementId,
  signatory,
  onClose,
}: {
  engagementId: string;
  signatory: string;
  onClose: () => void;
}) {
  const { toast } = useToast();
  const resend = useResendSignoffMutation(engagementId);
  return (
    <WorkspaceDialog
      open
      onOpenChange={(open) => !open && onClose()}
      title="Resend the sign-off link"
      description={`${signatory} gets a new link by email. The link sent before stops working.`}
      actions={
        <>
          <WorkspaceButton variant="tertiary" onClick={onClose} disabled={resend.isPending}>
            Cancel
          </WorkspaceButton>
          <WorkspaceButton
            variant="primary"
            disabled={resend.isPending}
            onClick={() =>
              resend.mutate(undefined, {
                onSuccess: () => {
                  toast({ message: `A new link went to ${signatory}.` });
                  onClose();
                },
              })
            }
          >
            {resend.isPending ? 'Sending…' : 'Resend link'}
          </WorkspaceButton>
        </>
      }
    >
      <p className="text-body-sm text-ink-700">The snapshot and what they sign stay the same.</p>
      {resend.error ? <ErrorMessage error={resend.error} /> : null}
    </WorkspaceDialog>
  );
}

export function CancelSignoffDialog({
  engagementId,
  signatory,
  onClose,
}: {
  engagementId: string;
  signatory: string;
  onClose: () => void;
}) {
  const { toast } = useToast();
  const cancel = useCancelSignoffMutation(engagementId);
  return (
    <WorkspaceDialog
      open
      onOpenChange={(open) => !open && onClose()}
      title="Cancel the sign-off request"
      description={`The link sent to ${signatory} stops working. The frozen snapshot is kept; you can request sign-off again later.`}
      actions={
        <>
          <WorkspaceButton variant="tertiary" onClick={onClose} disabled={cancel.isPending}>
            Keep the request
          </WorkspaceButton>
          <WorkspaceButton
            variant="destructive"
            disabled={cancel.isPending}
            onClick={() =>
              cancel.mutate(undefined, {
                onSuccess: () => {
                  toast({ message: 'Sign-off request cancelled. The link no longer works.' });
                  onClose();
                },
              })
            }
          >
            {cancel.isPending ? 'Cancelling…' : 'Cancel request'}
          </WorkspaceButton>
        </>
      }
    >
      {cancel.error ? <ErrorMessage error={cancel.error} /> : null}
    </WorkspaceDialog>
  );
}

/** `.cite-grid`: label and value rows. */
function WhoGrid({ rows }: { rows: ReadonlyArray<readonly [string, string]> }) {
  return (
    <dl className="grid grid-cols-[112px_1fr] gap-x-3 gap-y-1.5 rounded-card border border-ink-100 bg-paper-50 px-3 py-2.5 text-body-sm">
      {rows.map(([k, v]) => (
        <React.Fragment key={k}>
          <dt className="text-meta text-ink-500">{k}</dt>
          <dd className="min-w-0 text-ink-900">{v}</dd>
        </React.Fragment>
      ))}
    </dl>
  );
}
